/** Shared layout for public document views (share links and published docs): no app shell, no sign-in needed */

import { useEffect } from 'react'
import { AgentWikiLogo } from '../brand/agentwiki-logo'
import { useT } from '../../i18n'
import { MarkdownRenderer } from './markdown-renderer'

interface PublicDocLayoutProps {
  title: string
  content: string
  category?: string
  /** Kind of page shown next to the logo, e.g. "Shared document" */
  label: string
  /** Shown when the document cannot be loaded */
  errorMessage: string
  isLoading: boolean
  error: boolean
}

export function PublicDocLayout({ title, content, category, label, errorMessage, isLoading, error }: PublicDocLayoutProps) {
  const t = useT()
  useEffect(() => {
    if (title) document.title = `${title} · AgentWiki`
    return () => {
      document.title = 'AgentWiki'
    }
  }, [title])

  if (isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-surface" role="status" aria-label={t('common.loading')}>
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-border border-t-accent" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-3 bg-surface px-4 text-center">
        <p role="alert" className="text-sm text-fg-muted">
          {errorMessage}
        </p>
        <a href="/" className="text-sm text-accent-text hover:underline">
          {t('sharing.public.home')}
        </a>
      </div>
    )
  }

  return (
    <div className="min-h-dvh bg-surface text-fg">
      <header className="flex items-center justify-between gap-3 border-b border-border px-4 py-3 md:px-8 md:py-4">
        <div className="flex min-w-0 items-center gap-3">
          <a href="/" aria-label="AgentWiki">
            <AgentWikiLogo />
          </a>
          <span className="truncate text-xs text-fg-subtle">{label}</span>
        </div>
        {category && <span className="shrink-0 rounded-md border border-border px-2 py-0.5 text-xs text-fg-subtle">{category}</span>}
      </header>
      <main className="px-4 py-6 md:px-8 md:py-8">
        <article className="mx-auto max-w-3xl">
          <h1 className="mb-6 break-words font-display text-3xl font-semibold tracking-tight text-fg">{title}</h1>
          <MarkdownRenderer content={content} />
        </article>
      </main>
    </div>
  )
}
